import React from "react"
import {UserOutlined, EditOutlined} from "@ant-design/icons"

// ─────────────────────────────────────────────────────────────────────
//  Bandeau « utiliser mon compte » (prefill paiement + formulaires).
//
//  Remplace l'Alert Antd par un bandeau doux à deux états (toggle), partagé entre
//  le tunnel de paiement et le bloc identité des formulaires :
//    - mode "active"   : compte connecté, identité pré-remplie/verrouillée.
//                        Bouton « Ce n'est pas moi » → onAction (désactiver).
//    - mode "disabled" : le visiteur a choisi de saisir d'autres informations.
//                        Bouton « Réutiliser mon compte » → onAction (réactiver).
//
//  RESPONSIVE AUTONOME : sur écran étroit (≤ 480px), l'action passe SOUS le texte
//  (au lieu de rester à droite et de comprimer le titre sur 4 lignes). Géré par une
//  vraie media query (style scoped injecté une fois) → pas besoin de prop isMobile,
//  identique web ET admin.
//
//  Composant PUR de présentation : tous les textes sont passés en props (l'i18n
//  `t`` ` vit dans les appelants). Aucune dépendance hors React + icônes Antd.
// ─────────────────────────────────────────────────────────────────────

const PALETTE = {
    active: {
        bg: "#E6F1FB",          // info 50
        bar: "#185FA5",         // info 600
        chip: "#B5D4F4",        // info 100
        title: "#0C447C",       // info 800
        sub: "#185FA5",         // info 600
        link: "#185FA5",
    },
    disabled: {
        bg: "#FAEEDA",          // amber 50
        bar: "#BA7517",         // amber 400/600
        chip: "#FAC775",        // amber 100
        title: "#633806",       // amber 800
        sub: "#854F0B",         // amber 600
        link: "#854F0B",
    },
}

// Feuille de style scoped (injectée une fois). Le responsive vit ici (impossible en
// inline). `.acpb` = ligne par défaut ; sous 480px on empile l'action sous le texte.
const ACPB_CSS = `
.acpb {
    display: flex;
    align-items: center;
    gap: 11px;
    border-radius: 12px;
    padding: 11px 14px;
    margin-bottom: 16px;
}
.acpb-icon {
    width: 30px; height: 30px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.acpb-body { flex: 1 1 auto; min-width: 0; }
.acpb-title { font-size: 13.5px; font-weight: 500; line-height: 1.3; }
.acpb-sub { font-size: 12px; margin-top: 1px; }
.acpb-action {
    font-size: 13px; font-weight: 500; cursor: pointer;
    text-decoration: underline; text-underline-offset: 2px; white-space: nowrap;
    flex-shrink: 0;
}
@media (max-width: 480px) {
    .acpb {
        flex-wrap: wrap;
        align-items: flex-start;
        row-gap: 8px;
    }
    /* le texte garde sa ligne avec l'icône ; l'action passe en dessous, alignée
       sous le texte (décalée de la largeur icône + gap pour rester propre). */
    .acpb-action {
        flex-basis: 100%;
        margin-left: 41px;
    }
}
`

let __acpbStyleInjected = false
function injectStyleOnce() {
    if (typeof document === "undefined" || __acpbStyleInjected) return
    const el = document.createElement("style")
    el.setAttribute("data-acpb", "1")
    el.appendChild(document.createTextNode(ACPB_CSS))
    document.head.appendChild(el)
    __acpbStyleInjected = true
}

export default function AccountPrefillBanner({
    mode = "active",            // "active" | "disabled"
    title,                      // ligne 1 (ex: « Connecté en tant que … »)
    subtitle,                   // ligne 2 (ex: « Vos informations sont pré-remplies »)
    actionLabel,                // libellé du lien (ex: « Ce n'est pas moi »)
    onAction,                   // callback du lien
    style,
}) {
    injectStyleOnce()
    const c = PALETTE[mode] || PALETTE.active
    const Icon = mode === "disabled" ? EditOutlined : UserOutlined

    return (
        <div className="acpb" style={{background: c.bg, borderLeft: "3px solid " + c.bar, ...style}}>
            <div className="acpb-icon" style={{background: c.chip}}>
                <Icon style={{fontSize: 16, color: c.title}}/>
            </div>

            <div className="acpb-body">
                <div className="acpb-title" style={{color: c.title}}>{title}</div>
                {subtitle && <div className="acpb-sub" style={{color: c.sub}}>{subtitle}</div>}
            </div>

            {actionLabel && onAction && (
                <a
                    className="acpb-action"
                    role="button"
                    tabIndex={0}
                    onClick={onAction}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAction() } }}
                    style={{color: c.link}}
                >
                    {actionLabel}
                </a>
            )}
        </div>
    )
}
